import { FC, useEffect } from "react";
import {
  CoreFieldProps,
  CurrencyField,
  FieldFormV2,
  FieldV2,
  LoadingV2,
  NumericField,
  ReadOnlyField,
  SectionHeader,
} from "@ucl/library";
import { useFinancialApplicationForm } from "@/hooks/useFinancialApplicationForm";
import { FinancialApplication } from "@/types/FinancialApplication";
import { useAuthenticatedApiClient } from "@/hooks/useAuthenticatedApiClient";
import FinancialApplicationApiClient from "@/api/financialApplicationApiClient";
import { useFinancialApplicationCoreData } from "@/hooks/useFinancialApplicationCoreData";
import {
  useFinancialApplicationFormSectionValidation,
  FinancialApplicationFormValidationDisplayBehavior,
  FinancialApplicationFormSectionContextType,
} from "@/hooks/useFinancialApplicationFormSectionValidation";
import { FinancialApplicationSection } from "@/types/enums";
import "./AccountDetailsForm.scss";
import { FIELD_KEYS } from "@/constants";
import { useFinancialProducts } from "@/hooks/useFinancialProducts";

export interface AccountDetailsFormProps {
  financialApplicationId: string;
  autoValidationBehavior?: FinancialApplicationFormValidationDisplayBehavior;
  availableFields?: string[];
  showHeader?: boolean;
  validationContextType: FinancialApplicationFormSectionContextType;
}

export const AccountDetailsForm: FC<AccountDetailsFormProps> = (props) => {
  const {
    formValue,
    setFormValue,
    saveFormData,
    schemaFactory,
    formBuilder,
    fieldFormModule,
    enqueueAfterNextSave,
    updateDepositApplicationAmount,
  } = useFinancialApplicationForm(props.financialApplicationId, props.validationContextType);

  const { validationErrors, getCollectionValidationErrors } =
    useFinancialApplicationFormSectionValidation(
      props.financialApplicationId,
      FinancialApplicationSection.AccountDetails,
      props.autoValidationBehavior,
      props.validationContextType
    );

  const depositApplicationErrors = getCollectionValidationErrors("DepositApplications");

  const financialApplicationApiClient = useAuthenticatedApiClient(FinancialApplicationApiClient);
  const { products } = useFinancialProducts(financialApplicationApiClient);
  const { coreData, conditionalFlags, refreshCoreData } = useFinancialApplicationCoreData(
    props.financialApplicationId
  );

  const showBusinessAccountDetails =
    !!coreData?.hasBusinessAccountSelected || !!coreData?.hasIoltaAccountSelected;
  const showPersonalAccountDetails = !!coreData?.hasPersonalAccountSelected;

  // Refetch core data when form values change to update conditional flags
  useEffect(() => {
    if (formValue) {
      enqueueAfterNextSave(async () => {
        await refreshCoreData();
      });
    }
  }, [formValue?.willHaveJointAccountOwners, formValue?.willHavePersonalAccountBeneficiaries]);

  if (!schemaFactory || !formBuilder || !formValue) {
    return (
      <section className="account-details-form">
        <LoadingV2 />
      </section>
    );
  }

  // Shared mapped and filtered deposit applications
  const mappedDepositApplications =
    formValue.depositApplications
      ?.map((depositApp) => {
        // Find the product for this deposit application
        const product = products.find((p) => p.id === depositApp.financialProductId);
        return {
          ...depositApp,
          productName: product?.productName,
          isBusinessProduct: product?.isBusinessProduct,
          isIoltaProduct: product?.isIoltaProduct,
          isPersonalProduct: product?.isPersonalProduct,
          isDepositProduct: product?.isDepositProduct,
          isCreditProduct: product?.isCreditProduct,
          termInMonths: product?.termInMonths,
          interestRateType: product?.interestRateType,
          interestRateIndex: product?.interestRateIndex,
          indexInBasisPoints: product?.indexInBasisPoints,
          indexEffectiveDate: product?.indexEffectiveDate,
          feeAmount: product?.feeAmount,
        };
      })
      .filter((depositApp) => !!depositApp.productName)
      .filter((depositApp) => depositApp.isActive) || [];

  // Filtered groups for conditional rendering
  const businessDepositProducts = mappedDepositApplications.filter(
    (depositApp) =>
      (depositApp.isBusinessProduct || depositApp.isIoltaProduct) && depositApp.isDepositProduct
  );
  const businessCreditProducts = mappedDepositApplications.filter(
    (depositApp) =>
      (depositApp.isBusinessProduct || depositApp.isIoltaProduct) && depositApp.isCreditProduct
  );
  const personalDepositProducts = mappedDepositApplications.filter(
    (depositApp) => depositApp.isPersonalProduct && depositApp.isDepositProduct
  );

  const keyPersonRolesInAvailableFields =
    props.availableFields?.includes(FIELD_KEYS.FINANCIAL_APPLICATION.IS_BENEFICIAL_OWNER) ||
    props.availableFields?.includes(FIELD_KEYS.FINANCIAL_APPLICATION.IS_AUTHORIZED_SIGNER) ||
    props.availableFields?.includes(FIELD_KEYS.FINANCIAL_APPLICATION.IS_CONTROL_PERSON);

  const shouldShowKeyPersonRoles =
    conditionalFlags.isKeyPeopleEnabled &&
    ((props.availableFields || []).length <= 0 || keyPersonRolesInAvailableFields);

  return (
    <div className="account-details-form">
      <FieldFormV2<FinancialApplication>
        ref={fieldFormModule}
        value={formValue}
        fieldSchemaFormBuilder={formBuilder}
        onFormSubmit={async (value) => {
          await saveFormData(value);
        }}
        onFieldChanged={(newValue, newFieldValue, _fieldProps) => {
          setFormValue(newValue, newFieldValue);
        }}
        errors={validationErrors}
        availableFields={props.availableFields}
      >
        {/* Business Account Details */}
        {showBusinessAccountDetails && (
          <section className="account-details-form__section business-account-details-section">
            <SectionHeader label="Business Account Details" hidden={!props.showHeader} />
            {(props.availableFields || [])?.length <= 0 && ( // This section is not available thru availableFields
              <>
                {businessDepositProducts.length > 0 && (
                  <section className="account-details-form__section__content business-account-details-section__content">
                    {businessDepositProducts.map((depositApp) => {
                      return (
                        <CurrencyField
                          key={depositApp.id}
                          label={`Initial Deposit Amount - ${depositApp.productName}`}
                          value={depositApp.initialDepositAmount}
                          errorMessages={
                            depositApplicationErrors.fieldSchemaCollectionMemberValidationErrors[
                              depositApp.id
                            ]?.["InitialDepositAmount"]
                          }
                          onSubmit={(amount) => {
                            updateDepositApplicationAmount(
                              depositApp.id,
                              amount || 0,
                              "initialDepositAmount"
                            );
                          }}
                          showIncrementButtons={false}
                          isRequired
                        />
                      );
                    })}
                  </section>
                )}
                {businessCreditProducts.length > 0 &&
                  businessCreditProducts.map((depositApp) => (
                    <section
                      key={depositApp.id}
                      className="account-details-form__section__content business-account-details-section__content"
                    >
                      <CurrencyField
                        label={`Requested Amount - ${depositApp.productName}`}
                        value={depositApp.creditAmountRequested}
                        errorMessages={
                          depositApplicationErrors.fieldSchemaCollectionMemberValidationErrors[
                            depositApp.id
                          ]?.["CreditAmountRequested"]
                        }
                        onSubmit={(amount) => {
                          updateDepositApplicationAmount(
                            depositApp.id,
                            amount || 0,
                            "creditAmountRequested"
                          );
                        }}
                        showIncrementButtons={false}
                        isRequired
                      />
                      {/* TODO: The data coming back for these fields should not be visible to non-bankers in network tab of browser */}
                      {props.validationContextType ===
                        FinancialApplicationFormSectionContextType.Banker && (
                        <>
                          <CurrencyField
                            label={`Offered Amount - ${depositApp.productName}`}
                            value={depositApp.creditAmountOffered}
                            errorMessages={
                              depositApplicationErrors.fieldSchemaCollectionMemberValidationErrors[
                                depositApp.id
                              ]?.["CreditAmountOffered"]
                            }
                            onSubmit={(amount) => {
                              updateDepositApplicationAmount(
                                depositApp.id,
                                amount || 0,
                                "creditAmountOffered"
                              );
                            }}
                            showIncrementButtons={false}
                            isRequired
                          />
                          <NumericField
                            label={`Interest Rate Spread (bps) - ${depositApp.productName}`}
                            value={depositApp.interestRateSpreadInBasisPoints}
                            errorMessages={
                              depositApplicationErrors.fieldSchemaCollectionMemberValidationErrors[
                                depositApp.id
                              ]?.["InterestRateSpreadInBasisPoints"]
                            }
                            onSubmit={(value) => {
                              updateDepositApplicationAmount(
                                depositApp.id,
                                value || 0,
                                "interestRateSpreadInBasisPoints"
                              );
                            }}
                            isRequired
                            showIncrementButtons={false}
                          />
                          <ReadOnlyField
                            label={`Term in Months - ${depositApp.productName}`}
                            value={`${depositApp.termInMonths} months`}
                          />
                          <ReadOnlyField
                            label={`Interest Rate Type - ${depositApp.productName}`}
                            value={depositApp.interestRateType}
                          />
                          <ReadOnlyField
                            label={`Interest Rate Index - ${depositApp.productName}`}
                            value={depositApp.interestRateIndex}
                          />
                          <ReadOnlyField
                            label={
                              <span>
                                Index in Basis Points - {depositApp.productName} (
                                <a
                                  href="https://www.wsj.com/market-data/bonds/moneyrates?gaa_at=eafs&gaa_n=ASWzDAiq0dORe5f_byfTMrOl1LTxnomBqaLJw3oiEWsONeH8ZWasT9mzhI2s7xKb0MA%3D&gaa_ts=68d46e84&gaa_sig=46VKFVcqvgOwN0S38eoFShW2jOHEiCuW2j3qbOv2Id8aq0ui4_jb7QJhwHaI6PmHAPP6XBHNzOB6QMnVw6a-wA%3D%3D"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: "#0066cc", textDecoration: "underline" }}
                                >
                                  {depositApp.interestRateIndex}
                                </a>
                                )
                              </span>
                            }
                            description={`U.S. Effective Date: ${depositApp.indexEffectiveDate ? new Date(depositApp.indexEffectiveDate).toLocaleDateString() : "N/A"}`}
                            value={`${depositApp.indexInBasisPoints || 0} bps`}
                          />
                          <ReadOnlyField
                            key={`interest-rate-in-basis-points-${depositApp.interestRateSpreadInBasisPoints}`}
                            label={`Interest Rate in Basis Points - ${depositApp.productName}`}
                            value={`${(depositApp.indexInBasisPoints || 0) + (depositApp.interestRateSpreadInBasisPoints || 0)} bps`}
                          />
                          <ReadOnlyField
                            label={`Fee - ${depositApp.productName}`}
                            value={`$${(depositApp.feeAmount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                          />
                        </>
                      )}
                    </section>
                  ))}
              </>
            )}
            {/* Business Tax Return Revenue Fields */}
            {businessCreditProducts.length > 0 && (
              <section className="account-details-form__section__content business-account-details-section__content">
                <FieldV2
                  fieldProps={
                    {
                      fieldKey:
                        FIELD_KEYS.FINANCIAL_APPLICATION
                          .FIRST_YEAR_BUSINESS_TAX_RETURN_ANNUAL_REVENUE,
                      showIncrementButtons: false,
                    } as Partial<CoreFieldProps>
                  }
                />
                <FieldV2
                  fieldProps={
                    {
                      fieldKey:
                        FIELD_KEYS.FINANCIAL_APPLICATION
                          .SECOND_YEAR_BUSINESS_TAX_RETURN_ANNUAL_REVENUE,
                      showIncrementButtons: false,
                    } as Partial<CoreFieldProps>
                  }
                />
                 <ReadOnlyField
                   key={`two-year-average-of-business-tax-return-annual-revenue-${formValue.firstYearBusinessTaxReturnAnnualRevenue}-${formValue.secondYearBusinessTaxReturnAnnualRevenue}`}
                   label="Two-Year Average of Business Tax Return Annual Revenue"
                   value={`$${(((formValue.firstYearBusinessTaxReturnAnnualRevenue || 0) + (formValue.secondYearBusinessTaxReturnAnnualRevenue || 0)) / 2).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                 />
              </section>
            )}
            {shouldShowKeyPersonRoles && (
              <section className="account-details-form__section__content business-account-details-section__content">
                <FieldV2
                  fieldProps={
                    {
                      fieldKey: FIELD_KEYS.FINANCIAL_APPLICATION.IS_BENEFICIAL_OWNER,
                      showClearButton: true,
                    } as Partial<CoreFieldProps>
                  }
                />
                <FieldV2
                  fieldProps={
                    {
                      fieldKey: FIELD_KEYS.FINANCIAL_APPLICATION.IS_AUTHORIZED_SIGNER,
                      showClearButton: true,
                    } as Partial<CoreFieldProps>
                  }
                />
                <FieldV2
                  fieldProps={
                    {
                      fieldKey: FIELD_KEYS.FINANCIAL_APPLICATION.IS_CONTROL_PERSON,
                      showClearButton: true,
                    } as Partial<CoreFieldProps>
                  }
                />
              </section>
            )}
          </section>
        )}
        {showPersonalAccountDetails && (
          <section className="account-details-form__section personal-account-details-section">
            <SectionHeader label="Personal Account Details" hidden={!props.showHeader} />
            {(props.availableFields || [])?.length <= 0 && ( // This section is not available thru availableFields
              <>
                {personalDepositProducts.length > 0 && (
                  <section className="account-details-form__section__content">
                    {personalDepositProducts.map((depositApp) => {
                      return (
                        <CurrencyField
                          key={depositApp.id}
                          label={`Initial Deposit Amount - ${depositApp.productName}`}
                          value={depositApp.initialDepositAmount}
                          errorMessages={
                            depositApplicationErrors.fieldSchemaCollectionMemberValidationErrors[
                              depositApp.id
                            ]?.["InitialDepositAmount"]
                          }
                          onSubmit={(amount) => {
                            updateDepositApplicationAmount(
                              depositApp.id,
                              amount || 0,
                              "initialDepositAmount"
                            );
                          }}
                          showIncrementButtons={false}
                          isRequired
                        />
                      );
                    })}
                  </section>
                )}
              </>
            )}
            <section className="account-details-form__section__content personal-account-details-section__content">
              <FieldV2
                fieldProps={
                  {
                    fieldKey: FIELD_KEYS.FINANCIAL_APPLICATION.WILL_HAVE_JOINT_ACCOUNT_OWNERS,
                    showClearButton: true,
                  } as Partial<CoreFieldProps>
                }
              />
              <FieldV2
                fieldProps={
                  {
                    fieldKey:
                      FIELD_KEYS.FINANCIAL_APPLICATION.WILL_HAVE_PERSONAL_ACCOUNT_BENEFICIARIES,
                    showClearButton: true,
                  } as Partial<CoreFieldProps>
                }
              />
            </section>
          </section>
        )}
      </FieldFormV2>
    </div>
  );
};
